// Lightweight, dependency-free token estimator for chat UIs.
// Heuristic: ~4 chars/token for English-ish prose, with small per-message
// overhead for role + delimiter tokens (matches OpenAI's rough guidance).
// This is intentionally an estimate — not a tokenizer — kept fast for
// recomputing on every keystroke.

export type ChatLike = { role: string; content: string };

const PER_MSG_OVERHEAD = 4; // role + separators
const SYSTEM_PROMPT_OVERHEAD = 120; // matches the system prompt in hub-chat

export function estimateTokens(text: string): number {
  if (!text) return 0;
  // Rough but stable: 4 chars per token, min 1 token for non-empty content.
  return Math.max(1, Math.ceil(text.length / 4));
}

export function estimateConversationTokens(
  messages: ChatLike[],
  draft = "",
): number {
  let total = SYSTEM_PROMPT_OVERHEAD;
  for (const m of messages) {
    total += PER_MSG_OVERHEAD + estimateTokens(m.content);
  }
  if (draft) total += PER_MSG_OVERHEAD + estimateTokens(draft);
  return total;
}

// Practical input context windows per model (tokens). Conservative — leaves
// room for the response. We don't subtract max output here; the warning
// thresholds (75% / 90%) handle that buffer.
export const MODEL_CONTEXT: Record<string, number> = {
  "google/gemini-3-flash-preview": 1_000_000,
  "google/gemini-2.5-pro": 1_000_000,
  "google/gemini-2.5-flash": 1_000_000,
  "google/gemini-2.5-flash-lite": 1_000_000,
  "openai/gpt-5": 400_000,
  "openai/gpt-5-mini": 400_000,
  "openai/gpt-5-nano": 400_000,
  // Claude 4 family — 200k context across the board.
  "anthropic/claude-sonnet-4-5": 200_000,
  "anthropic/claude-opus-4": 200_000,
  "anthropic/claude-haiku-4-5": 200_000,
  // OpenRouter passthroughs — context windows sourced from each model's
  // public docs. Conservative when in doubt.
  "openrouter/meta-llama/llama-3.3-70b-instruct": 128_000,
  "openrouter/deepseek/deepseek-chat-v3.1": 128_000,
  "openrouter/deepseek/deepseek-r1": 128_000,
  "openrouter/qwen/qwen-2.5-72b-instruct": 128_000,
  "openrouter/mistralai/mistral-large-2411": 128_000,
  "openrouter/x-ai/grok-2-1212": 131_072,
};

export function getContextWindow(model: string): number {
  return MODEL_CONTEXT[model] ?? 128_000;
}

export type TokenLevel = "ok" | "warn" | "danger";

export function tokenLevel(used: number, ctx: number): TokenLevel {
  const ratio = used / ctx;
  if (ratio >= 0.9) return "danger";
  if (ratio >= 0.75) return "warn";
  return "ok";
}

export function formatTokens(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(n >= 10_000 ? 0 : 1) + "k";
  return String(n);
}

// Per-model pricing in USD per 1M tokens (input, output).
// Sourced from public list prices at time of writing — kept conservative
// and rounded; this is a UX estimate, not a billing figure.
export const MODEL_PRICING: Record<string, { in: number; out: number }> = {
  "google/gemini-3-flash-preview": { in: 0.3, out: 2.5 },
  "google/gemini-2.5-pro": { in: 1.25, out: 10 },
  "google/gemini-2.5-flash": { in: 0.3, out: 2.5 },
  "google/gemini-2.5-flash-lite": { in: 0.1, out: 0.4 },
  "openai/gpt-5": { in: 1.25, out: 10 },
  "openai/gpt-5-mini": { in: 0.25, out: 2 },
  "openai/gpt-5-nano": { in: 0.05, out: 0.4 },
  // Anthropic public list prices (USD per 1M tokens).
  "anthropic/claude-sonnet-4-5": { in: 3, out: 15 },
  "anthropic/claude-opus-4": { in: 15, out: 75 },
  "anthropic/claude-haiku-4-5": { in: 1, out: 5 },
  // OpenRouter list prices (USD per 1M tokens). OpenRouter passes pricing
  // through from the upstream provider; values rounded.
  "openrouter/meta-llama/llama-3.3-70b-instruct": { in: 0.13, out: 0.4 },
  "openrouter/deepseek/deepseek-chat-v3.1": { in: 0.27, out: 1.1 },
  "openrouter/deepseek/deepseek-r1": { in: 0.55, out: 2.19 },
  "openrouter/qwen/qwen-2.5-72b-instruct": { in: 0.35, out: 0.4 },
  "openrouter/mistralai/mistral-large-2411": { in: 2, out: 6 },
  "openrouter/x-ai/grok-2-1212": { in: 2, out: 10 },
};

// Average assistant reply length we assume when projecting "next turn" cost.
// Real replies vary wildly — this is just a sane default for a running gauge.
const ASSUMED_OUTPUT_TOKENS = 600;

export function getPricing(model: string): { in: number; out: number } {
  return MODEL_PRICING[model] ?? { in: 0, out: 0 };
}

/**
 * Estimate cost (USD) of sending the current conversation + draft to `model`.
 * Returns input cost (everything we'd send), projected output cost (assumed
 * reply length), and the sum. Costs scale linearly with token counts so this
 * stays cheap to recompute on every keystroke.
 */
export function estimateNextTurnCost(
  inputTokens: number,
  model: string,
  assumedOutput = ASSUMED_OUTPUT_TOKENS,
): { input: number; output: number; total: number } {
  const p = getPricing(model);
  const input = (inputTokens / 1_000_000) * p.in;
  const output = (assumedOutput / 1_000_000) * p.out;
  return { input, output, total: input + output };
}

export function formatUsd(n: number): string {
  if (n <= 0) return "$0";
  if (n < 0.01) return "<$0.01";
  if (n < 1) return `$${n.toFixed(3).replace(/0+$/, "").replace(/\.$/, "")}`;
  return `$${n.toFixed(2)}`;
}

// ---------------------------------------------------------------------------
// Points pricing (locked policy)
// ---------------------------------------------------------------------------
// Formula: points = ceil(raw_cost_usd × 1.5 × 1.10 × 100_000)
//   * 1.5  → 50 % gross margin
//   * 1.10 → 10 % platform fee per call
//   * 100_000 → 1 USD = 100 000 points (display unit)
// USD figures are intentionally NOT surfaced to the user — only the raw point
// total is shown so spend stays opaque while we always cover cost + margin.
export const POINTS_PER_USD = 100_000;
export const MARGIN_MULTIPLIER = 1.5;
export const PLATFORM_FEE_MULTIPLIER = 1.1;

export type UsageLike = {
  prompt_tokens?: number;
  completion_tokens?: number;
  completion_tokens_details?: { reasoning_tokens?: number };
};

/** Raw upstream USD cost for a single assistant turn (no margin). */
export function rawCostForUsage(model: string, usage: UsageLike): number {
  const p = getPricing(model);
  const inTok = usage.prompt_tokens ?? 0;
  const outTok = usage.completion_tokens ?? 0;
  // Reasoning tokens are billed as output tokens by every provider that
  // exposes them, so they're already included in completion_tokens — don't
  // double-count.
  return (inTok / 1_000_000) * p.in + (outTok / 1_000_000) * p.out;
}

/** Points charged for a single assistant turn (always profitable). */
export function pointsForUsage(model: string, usage: UsageLike): number {
  const raw = rawCostForUsage(model, usage);
  if (raw <= 0) return 0;
  return Math.ceil(raw * MARGIN_MULTIPLIER * PLATFORM_FEE_MULTIPLIER * POINTS_PER_USD);
}

export function formatPoints(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1) + "M";
  if (n >= 10_000) return (n / 1_000).toFixed(0) + "k";
  return n.toLocaleString();
}

